// 專業人士可排案時段的純函式（不依賴站台）。
// 時間一律以瀏覽器時間判斷（與 Calendar.vue 一致，假設使用者在台北時區）；
// 日後要改成明確換算 Asia/Taipei，只需修改本檔的 toDateKey / minuteOfDay / dateAtMinute。
import msg from './msg';

const DAY_MINUTES = 1440;

const pad2 = (n) => String(n).padStart(2, '0');

/**
 * JS getDay()（0=週日…6=週六）轉後端 weekday（1=週一…7=週日）。
 * @param {number} jsDay
 * @returns {number}
 */
export function jsDayToWeekday(jsDay) {
  return jsDay === 0 ? 7 : jsDay;
}

/**
 * 後端 weekday（1=週一…7=週日）轉 JS getDay()（0=週日…6=週六）。
 * @param {number} weekday
 * @returns {number}
 */
export function weekdayToJsDay(weekday) {
  return weekday === 7 ? 0 : weekday;
}

/**
 * Date 轉 YYYY-MM-DD（本地日期），對應假日與不可排案日期的比對。
 * @param {Date} date
 * @returns {string}
 */
export function toDateKey(date) {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

/**
 * "HH:MM" 轉當天分鐘數，"24:00" = 1440。
 * @param {string} str
 * @returns {number}
 */
export function timeToMinute(str) {
  const [h, m] = str.split(':').map(Number);
  return h * 60 + m;
}

/**
 * 當天分鐘數轉 "HH:MM"，1440 轉 "24:00"。
 * @param {number} minute
 * @returns {string}
 */
export function minuteToTime(minute) {
  return `${pad2(Math.floor(minute / 60))}:${pad2(minute % 60)}`;
}

/**
 * 七天是否有任何每週時段；false 代表未設定（全時段可排案）。
 * @param {?{weekly: Array}} availability
 * @returns {boolean}
 */
export function hasWeeklySlots(availability) {
  return !!(availability && availability.weekly && availability.weekly.length > 0);
}

/**
 * 合併重疊／相鄰的區間（分鐘），回傳依起點排序的新陣列。
 * @param {Array<{start: number, end: number}>} slots
 * @returns {Array<{start: number, end: number}>}
 */
export function mergeSlots(slots) {
  const sorted = slots.map((s) => ({ start: s.start, end: s.end })).sort((a, b) => a.start - b.start);
  const merged = [];
  sorted.forEach((s) => {
    const last = merged[merged.length - 1];
    if (last && s.start <= last.end) {
      last.end = Math.max(last.end, s.end);
    } else {
      merged.push(s);
    }
  });
  return merged;
}

/**
 * 依起點排序，合併相鄰且館別相同的時段；館別不同時即使相鄰也分開，供行事曆標示館別。
 * 後端不允許同一天時段重疊，因此只需處理相鄰。
 * @param {Array<{start: number, end: number, branchIds: number[]}>} slots
 * @returns {Array<{start: number, end: number, branchIds: number[]}>}
 */
export function mergeBranchSlots(slots) {
  const key = (ids) => [...ids].sort((a, b) => a - b).join(',');
  const sorted = slots
    .map((s) => ({ start: s.start, end: s.end, branchIds: [...s.branchIds] }))
    .sort((a, b) => a.start - b.start);
  const merged = [];
  sorted.forEach((s) => {
    const last = merged[merged.length - 1];
    if (last && s.start <= last.end && key(s.branchIds) === key(last.branchIds)) {
      last.end = Math.max(last.end, s.end);
    } else {
      merged.push(s);
    }
  });
  return merged;
}

/**
 * 不可排案原因的顯示文字；未知的值原樣回傳。
 * @param {?string} reason assigned_work / self_scheduled_work / personal
 * @returns {string}
 */
export function reasonText(reason) {
  return msg.availability.reasons[reason] || reason || '';
}

/**
 * 時段文字，如「14:00–18:00」。
 * @param {{start: number, end: number}} range 分鐘
 * @returns {string}
 */
function rangeText(range) {
  return `${minuteToTime(range.start)}–${minuteToTime(range.end)}`;
}

/**
 * 部分不可排案時段的描述，如「14:00–18:00（私人因素）、19:00–20:00（工作衝突-自排工作）」。
 * @param {Array<{start: number, end: number, reason: string}>} partialOff
 * @returns {string}
 */
export function describePartialOff(partialOff) {
  return partialOff
    .map((p) => `${rangeText(p)}（${reasonText(p.reason)}）`)
    .join(msg.availability.hint.slotSeparator);
}

/**
 * 計算每一天的狀態，給行事曆與即時提示使用。
 * @param {Date[]} dates 本地午夜的 Date
 * @param {?object} availability 後端 availability；null 時不產生時段與不可排案資訊（仍標示國定假日）
 * @param {Array<{date: string, name: string}>} holidays
 * @returns {Array<{dateKey: string, isHoliday: boolean, holidayName: string, isUnavailable: boolean,
 *   reason: string, isOff: boolean, slots: Array<{start: number, end: number}>,
 *   branchSlots: Array<{start: number, end: number, branchIds: number[]}>,
 *   partialOff: Array<{start: number, end: number, reason: string}>}>} 與 dates 對齊；
 *   isUnavailable / reason 為整天不可排案（reason 為顯示文字）；partialOff 為當天的部分不可排案時段（分鐘，依起點排序）；
 *   slots 為分鐘且已合併；branchSlots 為同一批時段但館別不同時不合併（branchIds 空陣列代表未指定）；
 *   isOff 已考慮「國定假日照常」
 */
export function buildDayInfo(dates, availability, holidays) {
  const holidayMap = {};
  (holidays || []).forEach((h) => {
    holidayMap[h.date] = h.name;
  });
  const holidaysAvailable = !!(availability && availability.holidays_available);
  return dates.map((date) => {
    const dateKey = toDateKey(date);
    const isHoliday = Object.prototype.hasOwnProperty.call(holidayMap, dateKey);
    const entries = availability
      ? (availability.unavailable || []).filter((u) => u.start_date <= dateKey && dateKey <= u.end_date)
      : [];
    // start / end 為 null 代表整天
    const unavailable = entries.find((u) => !u.start);
    const partialOff = entries
      .filter((u) => !!u.start)
      .map((u) => ({ start: timeToMinute(u.start), end: timeToMinute(u.end), reason: u.reason }))
      .sort((a, b) => a.start - b.start);
    const weekday = jsDayToWeekday(date.getDay());
    const weekly = availability
      ? (availability.weekly || [])
        .filter((w) => w.weekday === weekday)
        .map((w) => ({ start: timeToMinute(w.start), end: timeToMinute(w.end), branchIds: w.branch_ids || [] }))
      : [];
    return {
      dateKey,
      isHoliday,
      holidayName: isHoliday ? holidayMap[dateKey] : '',
      isUnavailable: !!unavailable,
      reason: unavailable ? reasonText(unavailable.reason) : '',
      isOff: !!unavailable || (isHoliday && !holidaysAvailable),
      slots: mergeSlots(weekly),
      branchSlots: mergeBranchSlots(weekly),
      partialOff,
    };
  });
}

function describeSlots(dayInfo, configured) {
  const { hint } = msg.availability;
  if (!configured) {
    return hint.unconfigured;
  }
  if (dayInfo.slots.length === 0) {
    return hint.noSlotToday;
  }
  return `${hint.slots}${dayInfo.slots.map(rangeText).join(hint.slotSeparator)}`;
}

/**
 * 即時提示文字。順序：不可排案日期 → 國定假日（整天不可排案時）→ 每週時段，再附上部分不可排案時段。
 * @param {object} dayInfo buildDayInfo 的單日結果
 * @param {boolean} configured 是否已設定每週時段（hasWeeklySlots）
 * @returns {string}
 */
export function describeDay(dayInfo, configured) {
  const { hint } = msg.availability;
  if (dayInfo.isUnavailable) {
    return dayInfo.reason ? `${hint.unavailable}：${dayInfo.reason}` : hint.unavailable;
  }
  if (dayInfo.isOff) {
    return `${hint.holiday}：${dayInfo.holidayName}`;
  }
  const text = describeSlots(dayInfo, configured);
  if (dayInfo.partialOff.length === 0) {
    return text;
  }
  return `${text}；${hint.partialUnavailable}：${describePartialOff(dayInfo.partialOff)}`;
}

/**
 * 檢查預約時間是否落在不可排案日期、部分不可排案時段、國定假日或可排案時段外（僅供提醒，不阻擋）。
 * 跨午夜的預約會逐日檢查；未設定每週時段時不檢查「不在時段內」。
 * @param {{availability: ?object, holidays: Array, startAt: number, endAt: number}} params startAt / endAt 為 unix 秒
 * @returns {string[]} 原因（已去重），空陣列代表無提醒
 */
export function checkAppointmentAvailability({
  availability, holidays, startAt, endAt,
}) {
  if (!availability || !(endAt > startAt)) {
    return [];
  }
  const reasons = [];
  const add = (r) => {
    if (!reasons.includes(r)) reasons.push(r);
  };
  const start = new Date(startAt * 1000);
  const end = new Date(endAt * 1000);
  const first = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const dates = [];
  for (let d = first; d < end; d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)) {
    dates.push(d);
  }
  const infos = buildDayInfo(dates, availability, holidays);
  const configured = hasWeeklySlots(availability);
  const { reminder } = msg.availability;
  infos.forEach((info, i) => {
    if (info.isUnavailable) {
      add(info.reason ? `${reminder.unavailable}（${info.reason}）` : reminder.unavailable);
      return;
    }
    if (info.isOff) {
      add(reminder.holiday);
      return;
    }
    const dayStart = dates[i].getTime();
    const from = Math.max(0, Math.round((start.getTime() - dayStart) / 60000));
    const to = Math.min(DAY_MINUTES, Math.round((end.getTime() - dayStart) / 60000));
    const overlapped = info.partialOff.filter((p) => p.start < to && from < p.end);
    if (overlapped.length > 0) {
      add(`${reminder.partialUnavailable}：${describePartialOff(overlapped)}`);
    }
    if (configured && !info.slots.some((s) => s.start <= from && to <= s.end)) {
      add(reminder.outsideSlot);
    }
  });
  return reasons;
}

/**
 * 更新者描述，如「由 王小明」或「由 管理員 以 專業人士 身份」；無資料回傳空字串。
 * @param {?{name: string, impersonated_by: ?string}} updatedBy
 * @returns {string}
 */
export function describeUpdatedBy(updatedBy) {
  if (!updatedBy || !updatedBy.name) {
    return '';
  }
  if (updatedBy.impersonated_by) {
    return `由 ${updatedBy.impersonated_by} 以 ${updatedBy.name} 身份`;
  }
  return `由 ${updatedBy.name}`;
}
